// Inline place card: auto-injected by ArticleBodyWithCards under a blog
// section whose body links to a venue we list (owner-approved design #59).
// A framed mini card: photo, name, one money line and a "See the place" link,
// small enough to sit between paragraphs without taking over the article.
//
// The money line follows what the place is (CLAUDE.md "Money consistency"):
// - bookable restaurant: "Free to reserve", never a price;
// - explore (editorial) place: no price and no booking word at all;
// - everything else: "From X" in the visitor's currency, when it has a price.
//
// Data prep lives in the shared `useActivityVm` hook (same query cache as the
// lab concepts in ../concepts).
import { useTranslation } from "react-i18next";
import { CONTROL_EDGE, CONTROL_LIT } from "@majourneys/shared/ui";
import { Icon } from "@majourneys/icons";
import { useDisplayPrice } from "../../../lib/currency";
import { panelStyle } from "../../../shared/ui/card-frame";
import { useActivityVm, type ActivityVm } from "../concepts/use-activity-vm";

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const GREEN = "#1A7A44";

interface InlineActivityCardProps {
  readonly slug: string;
  readonly locale?: string;
}

export type InlineMoneyLine =
  | { kind: "free" }
  | { kind: "from"; price: number }
  | { kind: "none" };

/** What the card's second line says about money, by what the place is. */
export function inlineMoneyLine(vm: Pick<ActivityVm, "mode" | "isRestaurant" | "price">): InlineMoneyLine {
  if (vm.mode === "explore") return { kind: "none" };
  if (vm.isRestaurant) return { kind: "free" };
  if (vm.price > 0) return { kind: "from", price: vm.price };
  return { kind: "none" };
}

export function InlineActivityCard({ slug, locale = "en" }: InlineActivityCardProps) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPrice();
  const vm = useActivityVm(slug, locale, { allowExplore: true });
  if (!vm) return null;

  const money = inlineMoneyLine(vm);
  const place = [vm.category, vm.city].filter(Boolean).join(" · ");

  return (
    <aside
      aria-label={`${t("blog.featured")}: ${vm.title}`}
      data-testid="inline-place-card"
      // [&_*]:!my-0 neutralises the article prose margins (`[&_p]:mt-4`,
      // `[&_img]:my-6`) that would otherwise push the rows apart.
      className="not-prose my-7 [&_*]:!my-0"
      style={{ ...panelStyle(16), padding: 6 }}
    >
      <div className="flex items-center gap-3">
        {vm.image ? (
          <img
            src={vm.image}
            alt=""
            loading="lazy"
            className="h-14 w-14 shrink-0 object-cover"
            style={{ borderRadius: 11 }}
          />
        ) : (
          <span aria-hidden className="h-14 w-14 shrink-0 bg-[rgba(17,14,57,0.06)]" style={{ borderRadius: 11 }} />
        )}
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 break-words font-sans text-[13.5px] font-semibold leading-tight" style={{ color: NAVY }}>
            {vm.title}
          </p>
          {money.kind === "free" ? (
            <p className="mt-0.5 break-words font-sans text-[11.5px] font-semibold" style={{ color: GREEN }}>
              {t("blog.free_to_reserve")}
            </p>
          ) : money.kind === "from" ? (
            <p className="mt-0.5 break-words font-sans text-[11.5px] font-semibold" style={{ color: NAVY }}>
              {t("blog.from_price", { price: displayPrice(money.price) })}
            </p>
          ) : place ? (
            <p className="mt-0.5 break-words font-sans text-[11.5px]" style={{ color: MUTED }}>
              {place}
            </p>
          ) : null}
        </div>
        <a
          href={vm.href}
          className="mj-press me-1 inline-flex min-h-[34px] shrink-0 items-center gap-1 rounded-full px-3.5 font-sans text-[12px] font-semibold"
          style={{ background: CONTROL_LIT, boxShadow: `0 0 0 1px ${CONTROL_EDGE}`, color: NAVY }}
        >
          {t("blog.see_place")}
          <Icon name={vm.isRtl ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"} className="text-[14px]" aria-hidden />
        </a>
      </div>
    </aside>
  );
}
